import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type { Folder } from "certiblok-api-manager";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { snapshot, subscribe, useSnapshot } from "valtio";
import MLDialog from "../../../components/poppers";
import { apiErrorParser } from "../../../hooks/api";
import { useTreeViewContext } from "../TreeViewController";
import { PER_PAGE } from "../constants/treeViewConstants";
import { computeRelativeElementIndex, getLastElementOfFolderIndex } from "../functions/treeViewFunctions";
import { treeViewQueryKeys } from "../queries/treeViewQueries";
import type { TreeViewListItem } from "../types/ListItem";
import { useToggleOpenFolder } from "./treeViewFolderHooks";
import { useTreeViewItemsList } from "./treeViewItemsHooks";

export const useTreeViewItem = (item: TreeViewListItem) => {
	const { itemsList, fetchFolderLayer, fetchRootLayer, openedFolders, rootFolderId, appliedFilters, ignoreQueryParams } =
		useTreeViewContext();

	const [relativeIndexFromFolder, setRelativeIndexFromFolder] = useState(() => {
		return computeRelativeElementIndex({ items: itemsList, item });
	});

	const [searchParams] = useSearchParams();
	const hasInitSearchParams = useRef(false);
	const toggleOpenFolder = useToggleOpenFolder();

	useEffect(() => {
		return subscribe(itemsList, () => {
			setRelativeIndexFromFolder(computeRelativeElementIndex({ items: itemsList, item }));
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [item]);

	const page = useMemo(() => {
		return Math.floor(relativeIndexFromFolder / PER_PAGE);
	}, [relativeIndexFromFolder]);

	const { data, isFetching } = useQuery({
		queryKey: treeViewQueryKeys.folder.withPagination({
			rootFolderId: rootFolderId,
			folderId: item.parentFolder.folderId,
			page,
			perPage: PER_PAGE,
			otherFilters: appliedFilters,
		}),
		queryFn: () => {
			return item.parentFolder.folderId === rootFolderId
				? fetchRootLayer({ page, perPage: PER_PAGE })
				: fetchFolderLayer({
						folderId: item.parentFolder.folderId,
						page,
						perPage: PER_PAGE,
				  });
		},
		staleTime: 1000 * 60 * 2,
		onError: (error: AxiosError) => {
			if (error.response?.status === 404) {
				const itemsWithParentFolder = itemsList.filter((elem) => elem.parentFolder.folderId === item.parentFolder.folderId);
				itemsWithParentFolder.forEach((elem) => {
					const itemIndex = itemsList.indexOf(elem);
					itemsList.splice(itemIndex, 1);
				});
			} else {
				MLDialog.showSnackbar(apiErrorParser(error), { variant: "error" });
			}
		},
		select: (data) => {
			return data.data;
		},
		keepPreviousData: true,
		retry: false,
	});

	useEffect(() => {
		if (!data || isFetching) {
			return;
		}
		const relativeIndexFromFolder = computeRelativeElementIndex({ items: itemsList, item });
		const itemIndex = relativeIndexFromFolder % PER_PAGE;
		const fetchedItem = data[itemIndex];
		if (fetchedItem) {
			item.item = fetchedItem;
			if (fetchedItem.isFolder) {
				const folderInOpenedFolder = openedFolders.get(item.id);
				if (folderInOpenedFolder) {
					const lastItemIndex = getLastElementOfFolderIndex({ items: itemsList, folder: item });

					const newItemTotalChildren = fetchedItem.totalRootChildren ?? folderInOpenedFolder.totalItems;
					if (newItemTotalChildren > folderInOpenedFolder.totalItems) {
						itemsList.splice(
							lastItemIndex + 1,
							0,
							...Array.from(Array(newItemTotalChildren - folderInOpenedFolder.totalItems)).map<TreeViewListItem>(() => {
								return {
									parentFolder: folderInOpenedFolder,
									isLoading: true,
									id: nanoid(),
								};
							})
						);
					} else if (newItemTotalChildren < folderInOpenedFolder.totalItems) {
						const itemsToRemoveCount = folderInOpenedFolder.totalItems - newItemTotalChildren;
						itemsList.splice(lastItemIndex - itemsToRemoveCount + 1, itemsToRemoveCount);
					}
					folderInOpenedFolder.totalItems = newItemTotalChildren;
				}
			}
			if (!hasInitSearchParams.current && !ignoreQueryParams) {
				hasInitSearchParams.current = true;
				const openedFolders = searchParams.get("openedFolders")?.trim().replaceAll(" ", "").split("#");
				const shouldOpen =
					openedFolders?.find((elem) => {
						try {
							const jsonElem = JSON.parse(elem) as { id: string; deep: number };
							return jsonElem.id === fetchedItem.id && jsonElem.deep === item.parentFolder.deepness + 1;
						} catch (e) {
							return false;
						}
					}) !== undefined;
				if (openedFolders && shouldOpen && fetchedItem.isFolder) {
					toggleOpenFolder({
						folder: { folderId: item.id, childrenCounts: fetchedItem.totalRootChildren ?? 0 },
						parentFolder: item.parentFolder,
						forceOpen: true,
					});
				}
			}
		}
		item.isLoading = false;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, isFetching]);

	return { item };
};

export function useParentFolderOfItem(item: TreeViewListItem) {
	const itemsList = useTreeViewItemsList();
	const itemSnap = useSnapshot(item);

	const [parentFolder, setParentFolder] = useState<Folder>();

	useEffect(() => {
		const parentFolderProxy = itemsList.find((item) => item.item?.id === itemSnap.parentFolder.folderId);
		if (parentFolderProxy) {
			const subscriptionCallback = () => {
				if (parentFolderProxy.item) {
					setParentFolder(snapshot(parentFolderProxy.item) as unknown as Folder);
				}
			};
			subscriptionCallback();
			return subscribe(parentFolderProxy, subscriptionCallback);
		}
	}, [itemSnap.parentFolder.folderId, itemsList]);

	return parentFolder;
}
